import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import DateRangeModal from './DateRangeModal';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

export default function DateFilter({ value, onChange }) {
  const { t } = useTranslation();

  const datePresets = useMemo(() => [
    { value: '24hours', label: t('Last 24 hours') },
    { value: '7days', label: t('Last 7 days') },
    { value: '30days', label: t('Last 30 days') },
    { value: '90days', label: t('Last 90 days') },
    { value: '180days', label: t('Last 180 days') },
    { value: '365days', label: t('Last 365 days') },
    { value: 'custom', label: t('📅 Custom range') }
  ], [t]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('7days');

  // Set default 7-day range on component mount
  useEffect(() => {
    if (!value) {
      const dates = getPresetDates('7days');
      onChange(dates);
    }
  }, []);

  const getPresetDates = (preset) => {
    let end = new Date();
    let start = new Date();

    switch (preset) {
      case '24hours':
        start.setDate(end.getDate() - 1);
        break;
      case '7days':
        start.setDate(end.getDate() - 7);
        break;
      case '30days':
        start.setDate(end.getDate() - 30);
        break;
      case '90days':
        start.setDate(end.getDate() - 90);
        break;
      case '180days':
        start.setDate(end.getDate() - 180);
        break;
      case '365days':
        start.setFullYear(end.getFullYear() - 1);
        break;
      default:
        return null;
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  };

  const isPresetDates = (value) => {
    if (!value) return false;

    const presets = {
      '24hours': getPresetDates('24hours'),
      '7days': getPresetDates('7days'),
      '30days': getPresetDates('30days'),
      '90days': getPresetDates('90days'),
      '180days': getPresetDates('180days'),
      '365days': getPresetDates('365days'),
    };

    return Object.entries(presets).find(([preset, dates]) =>
      dates.startDate === value.startDate && dates.endDate === value.endDate
    )?.[0];
  };

  const getCurrentLabel = () => {
    if (!value) return datePresets[0];

    const preset = isPresetDates(value);
    if (preset) {
      return datePresets.find(p => p.value === preset);
    }

    // If not a preset, it's a custom range
    const start = new Date(value.startDate);
    const end = new Date(value.endDate);
    const formatDate = (date) => dayjs(date).format('D MMM YYYY');

    return {
      value: 'custom',
      label: `${formatDate(start)} - ${formatDate(end)}`
    };
  };

  const handleChange = (selected) => {
    if (!selected) {
      const dates = getPresetDates('7days');
      setSelectedPreset('7days');
      onChange(dates);
      return;
    }

    if (selected.value === 'custom') {
      setShowModal(true);
    } else {
      setSelectedPreset(selected.value);
      const dates = getPresetDates(selected.value);
      onChange(dates);
    }
  };

  return (
    <>
      <Select
        value={getCurrentLabel()}
        onChange={handleChange}
        options={datePresets}
        className="text-sm"
        classNamePrefix="select"
        placeholder={t('Select a date range')}
        styles={{
          control: (base) => ({
            ...base,
            minHeight: '36px',
            height: 'auto',
            backgroundColor: 'var(--select-bg, #ffffff)',
            borderColor: 'var(--select-border, #d1d5db)',
            '&:hover': {
              borderColor: 'var(--select-border-hover, #9ca3af)'
            },
            '.dark &': {
              backgroundColor: 'var(--select-bg, #1e293b)',
              borderColor: 'var(--select-border, #475569)'
            }
          }),
          menu: (base) => ({
            ...base,
            backgroundColor: 'var(--select-bg, #ffffff)',
            border: '1px solid var(--select-border, #d1d5db)',
            zIndex: 999,
            '.dark &': {
              backgroundColor: 'var(--select-bg, #1e293b)',
              border: '1px solid var(--select-border, #475569)'
            }
          }),
          menuList: (base) => ({
            ...base,
            backgroundColor: 'var(--select-bg, #ffffff)',
            '.dark &': {
              backgroundColor: 'var(--select-bg, #1e293b)'
            }
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused
              ? 'var(--select-option-hover-bg, #f3f4f6)'
              : 'var(--select-bg, #ffffff)',
            color: 'var(--select-text, #111827)',
            '&:hover': {
              backgroundColor: 'var(--select-option-hover-bg, #f3f4f6)'
            },
            '.dark &': {
              backgroundColor: state.isFocused
                ? 'var(--select-option-hover-bg, #334155)'
                : 'var(--select-bg, #1e293b)',
              color: 'var(--select-text, #e2e8f0)',
              '&:hover': {
                backgroundColor: 'var(--select-option-hover-bg, #334155)'
              }
            }
          }),
          singleValue: (base) => ({
            ...base,
            color: 'var(--select-text, #111827)',
            '.dark &': {
              color: 'var(--select-text, #e2e8f0)'
            }
          }),
          multiValue: (base) => ({
            ...base,
            margin: '2px',
            backgroundColor: 'var(--select-multi-bg, #e5e7eb)',
            '.dark &': {
              backgroundColor: 'var(--select-multi-bg, #334155)'
            }
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: 'var(--select-text, #111827)',
            '.dark &': {
              color: 'var(--select-text, #e2e8f0)'
            }
          }),
          input: (base) => ({
            ...base,
            margin: '0px',
            color: 'var(--select-text, #111827)',
            '.dark &': {
              color: 'var(--select-text, #e2e8f0)'
            }
          }),
          valueContainer: (base) => ({
            ...base,
            backgroundColor: 'var(--select-bg, #ffffff)',
            '.dark &': {
              backgroundColor: 'var(--select-bg, #1e293b)'
            }
          }),
          placeholder: (base) => ({
            ...base,
            color: 'var(--select-placeholder, #6b7280)',
            '.dark &': {
              color: 'var(--select-placeholder, #64748b)'
            }
          })
        }}
      />

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center
          justify-center z-[60]">
          <DateRangeModal
            value={value}
            onChange={(dates) => {
              setSelectedPreset('custom');
              onChange(dates);
            }}
            onClose={() => setShowModal(false)}
          />
        </div>
      )}
    </>
  );
}